/**
 * @fileoverview ローディングスケルトンコンポーネント
 * @description チャット読み込み中のスケルトン表示
 */
import { cn } from '@/lib/utils'

/**
 * スケルトンのprops型
 */
interface SkeletonProps {
  /** カスタムクラス名 */
  className?: string
}

/**
 * 基本スケルトンコンポーネント
 */
export function Skeleton({ className }: SkeletonProps) {
  return <div className={cn('animate-pulse rounded-md bg-[#E5E4E1]', className)} />
}

/**
 * テンプレートカードスケルトン
 */
export function TemplateCardSkeleton({ className }: SkeletonProps) {
  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-3xl border border-[#E5E4E1] bg-white px-4 py-3',
        className
      )}
    >
      <Skeleton className="h-4 w-4 rounded-full" />
      <Skeleton className="h-4 w-32" />
    </div>
  )
}

/**
 * テンプレートグリッドスケルトン
 */
export function TemplateGridSkeleton({
  count = 6,
  className,
}: {
  count?: number
  className?: string
}) {
  return (
    <div className={cn('flex flex-wrap gap-2', className)}>
      {Array.from({ length: count }).map((_, i) => (
        <TemplateCardSkeleton key={i} />
      ))}
    </div>
  )
}

/**
 * メッセージバブルスケルトン
 */
export function MessageSkeleton({
  isUser = false,
  className,
}: {
  isUser?: boolean
  className?: string
}) {
  return (
    <div className={cn('flex flex-col gap-2', isUser ? 'items-end' : 'items-start', className)}>
      {!isUser && (
        <div className="flex items-center gap-2">
          <Skeleton className="h-12 w-12 rounded-full" />
          <Skeleton className="h-4 w-24" />
        </div>
      )}
      <div className={cn('rounded-2xl px-4 py-4', isUser ? 'bg-[#3D8A5A]/20' : 'bg-white')}>
        <Skeleton className="h-4 w-48 mb-2" />
        <Skeleton className="h-4 w-40 mb-2" />
        <Skeleton className="h-4 w-32" />
      </div>
    </div>
  )
}

/**
 * メッセージ一覧スケルトン
 */
export function MessageListSkeleton({
  count = 3,
  className,
}: {
  count?: number
  className?: string
}) {
  return (
    <div className={cn('flex flex-col gap-4', className)}>
      {Array.from({ length: count }).map((_, i) => (
        <MessageSkeleton key={i} isUser={i % 2 === 0} />
      ))}
    </div>
  )
}

/**
 * ウェルカムセクションスケルトン
 */
export function WelcomeSectionSkeleton({ className }: SkeletonProps) {
  return (
    <div className={cn('flex flex-col items-center gap-5', className)}>
      {/* アイコン */}
      <Skeleton className="h-16 w-16 rounded-full" />

      {/* テキスト */}
      <div className="flex flex-col items-center gap-2">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-4 w-64" />
        <Skeleton className="h-4 w-40" />
      </div>

      {/* テンプレート */}
      <div className="w-full pt-4">
        <Skeleton className="h-4 w-32 mb-3" />
        <TemplateGridSkeleton count={6} />
      </div>
    </div>
  )
}
